import { Avatar, Button, Switch, Table, Modal, Form, Input, message, Spin, Empty, Pagination, Grid } from 'antd'
import React, { useState, useEffect } from 'react'
import { EditOutlined, PlusOutlined, UserOutlined, MailOutlined, CalendarOutlined } from '@ant-design/icons'
import drEasyApi from '../../../Api/Customapi'

const { useBreakpoint } = Grid

function Secretary() {
  const screens = useBreakpoint()
  const isMobile = screens.md === false // < 768px

  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState(null)
  const [form] = Form.useForm()
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })

  useEffect(() => {
    fetchSecretaries()
  }, [])

  const fetchSecretaries = async (pageNumber = 1, pageSize = 10) => {
    setLoading(true)
    try {
      const res = await drEasyApi.get('/api/Centers/GetStaff', {
        params: { Role: 4, PageNumber: pageNumber, PageSize: pageSize },
      })
      const { staff, totalCount } = res.data
      setData(staff || [])
      setPagination((prev) => ({ ...prev, total: totalCount, current: pageNumber, pageSize }))
    } catch (err) {
      if (err?.response?.status === 403) {
        window.location.href = '/sub'
      } else {
        message.error('Failed to load secretaries')
      }
    } finally {
      setLoading(false)
    }
  }

  const openAdd = () => {
    setEditingRecord(null)
    form.resetFields()
    setModalOpen(true)
  }

  const openEdit = (record) => {
    setEditingRecord(record)
    form.setFieldsValue({
      firstName: record.name?.split(' ')[0] || '',
      lastName: record.name?.split(' ').slice(1).join(' ') || '',
      phoneNumber: record.phoneNumber || '',
      newPassword: '',
    })
    setModalOpen(true)
  }

  const handleSave = async () => {
    try {
      const values = await form.validateFields()

      if (editingRecord) {
        await drEasyApi.put('/api/Centers/EditSecertary', {
          secretaryId: editingRecord.userId,
          firstName: values.firstName,
          lastName: values.lastName,
          phoneNumber: values.phoneNumber,
          newPassword: values.newPassword || undefined,
        })
        message.success('Secretary updated successfully')
      } else {
        await drEasyApi.post('/api/Centers/AddSecertary', {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          password: values.password,
          phoneNumber: values.phoneNumber,
        })
        message.success('Secretary added successfully')
      }

      setModalOpen(false)
      fetchSecretaries(pagination.current, pagination.pageSize)
    } catch (err) {
      if (err?.errorFields) return // validation error, do nothing
      const errMsg = err?.response?.data?.message || 'Operation failed'
      message.error(errMsg)
    }
  }

  const handleToggleStatus = async (record) => {
    try {
      await drEasyApi.put('/api/Centers/ToggleSecertaryStatues', {
        secretaryId: record.userId,
      })
      setData((prev) =>
        prev.map((item) =>
          item.userId === record.userId ? { ...item, isDeactivated: !item.isDeactivated } : item
        )
      )
      message.success(
        record.isDeactivated ? 'Secretary activated' : 'Secretary deactivated'
      )
    } catch {
      message.error('Failed to update status')
    }
  }

  const handleTableChange = (pag) => {
    fetchSecretaries(pag.current, pag.pageSize)
  }

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString('en-GB') : '—'

  const renderStatusSwitch = (record) => (
    <Switch
      checked={!record.isDeactivated}
      onChange={() => handleToggleStatus(record)}
      checkedChildren="Active"
      unCheckedChildren="Inactive"
      style={{ backgroundColor: record.isDeactivated ? 'gray' : 'teal' }}
    />
  )

  // ─── Desktop / tablet columns ───────────────────────────────────────────────
  const columns = [
    {
      title: '#',
      key: 'index',
      width: 60,
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Joined',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: formatDate,
    },
    {
      title: 'Status',
      dataIndex: 'isDeactivated',
      key: 'isDeactivated',
      render: (_, record) => renderStatusSwitch(record),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
            title="Edit"
          />
        </div>
      ),
    },
  ]

  // ─── Mobile cards ───────────────────────────────────────────────────────────
  const renderMobileCards = () => (
    <Spin spinning={loading}>
      {!loading && data.length === 0 ? (
        <Empty className="py-10" />
      ) : (
        <div className="flex flex-col gap-3 min-h-[120px]">
          {data.map((record, index) => (
            <div
              key={record.userId}
              className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
            >
              <div className="h-1 bg-gradient-to-r from-primary to-primary/50" />
              <div className="p-3">
                {/* Header */}
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar size={48} icon={<UserOutlined />} className="shrink-0 bg-primary/20 text-primary" />
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-primary text-base leading-tight break-words">
                      {record.name}
                    </h3>
                    <span className="text-xs text-gray-400">
                      #{(pagination.current - 1) * pagination.pageSize + index + 1}
                    </span>
                  </div>
                </div>

                {/* Info */}
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <MailOutlined className="text-gray-400 shrink-0" />
                    <span className="text-sm text-gray-700 break-all min-w-0">{record.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarOutlined className="text-gray-400 shrink-0" />
                    <span className="text-sm text-gray-700">Joined {formatDate(record.createdAt)}</span>
                  </div>
                </div>

                {/* Footer: status + edit */}
                <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-gray-100">
                  {renderStatusSwitch(record)}
                  <Button
                    icon={<EditOutlined />}
                    className="text-primary border-primary"
                    onClick={() => openEdit(record)}
                  >
                    Edit
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {pagination.total > pagination.pageSize && (
        <div className="flex justify-center mt-4">
          <Pagination
            simple
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger={false}
            onChange={(page, pageSize) => {
              fetchSecretaries(page, pageSize)
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }}
          />
        </div>
      )}
    </Spin>
  )

  return (
    <div className="p-0 sm:p-4 md:p-6 overflow-x-hidden">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <h2 className="text-lg md:text-xl font-semibold">Secretaries Management</h2>
        <Button
          type="default"
          size={isMobile ? 'middle' : 'large'}
          block={isMobile}
          className="bg-gradient-to-r text-white from-primary to-primary_hover hover:bg-primary_hover md:hover:scale-105 hover:to-[#166575] border-0 shadow-lg hover:shadow-xl rounded-xl font-semibold transition-all"
          icon={<PlusOutlined />}
          onClick={openAdd}
        >
          Add Secretary
        </Button>
      </div>

      {isMobile ? (
        renderMobileCards()
      ) : (
        <Table
          dataSource={data}
          columns={columns}
          rowKey="userId"
          loading={loading}
          scroll={{ x: 700 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      )}

      <Modal
        title={editingRecord ? 'Edit Secretary' : 'Add Secretary'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText={editingRecord ? 'Update' : 'Add'}
        destroyOnClose
        centered
        width="min(520px, calc(100vw - 24px))"
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' } }}
        okButtonProps={{ style: { background: '#298E9F' } }}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-3">
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{ required: true, message: 'Please enter first name' }]}
            >
              <Input placeholder="First name" />
            </Form.Item>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[{ required: true, message: 'Please enter last name' }]}
            >
              <Input placeholder="Last name" />
            </Form.Item>
          </div>

          {!editingRecord && (
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Please enter an email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <Input placeholder="email@example.com" />
            </Form.Item>
          )}

          <Form.Item
            label="Phone Number"
            name="phoneNumber"
            rules={[{ required: true, message: 'Please enter a phone number' }]}
          >
            <Input placeholder="+20xxxxxxxxxx" />
          </Form.Item>

          <Form.Item
            label={editingRecord ? 'New Password (leave blank to keep current)' : 'Password'}
            name={editingRecord ? 'newPassword' : 'password'}
            rules={
              editingRecord
                ? []
                : [{ required: true, message: 'Please enter a password' }]
            }
          >
            <Input.Password placeholder="Password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Secretary